import { createContext, useContext, useState, useEffect, ReactNode, createElement } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  company?: string;
  created_at: string;
  updated_at: string;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  plan_name?: string;
  subscription_status?: string;
  plan_expires_at?: string | null;
  email_alerts_sent_this_month?: number;
  notification_email?: string | null;
}

export interface AuthState {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
}

export interface AuthContextType extends AuthState {
  signUp: (
    email: string,
    password: string,
    userData: {
      firstName: string;
      lastName: string;
      company?: string;
    },
    options?: { emailRedirectTo?: string }
  ) => Promise<{ data: any; error: any }>;
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<{ error: any }>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<{ data: any; error: any }>;
  resetPassword: (email: string) => Promise<{ data: any; error: any }>;
  updatePassword: (password: string) => Promise<{ data: any; error: any }>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    profile: null,
    session: null,
    loading: true
  });

  const loadUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        return;
      }

      setAuthState(prev => ({ ...prev, profile: data }));
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  useEffect(() => {
    const hasAuthParams = window.location.hash.includes('access_token=') || 
                          window.location.search.includes('code=') ||
                          window.location.hash.includes('type=recovery') ||
                          window.location.search.includes('type=recovery');

    console.log("useAuth Context [useEffect]: Initializing. hasAuthParams =", hasAuthParams);

    let authTimeout: NodeJS.Timeout | null = null;
    let isSubscribed = true;

    // 1. Obtener la sesión inicial de forma segura
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isSubscribed) return;

      console.log("useAuth Context: getSession() resolved -> session =", !!session);

      if (hasAuthParams && !session) {
        // Hay parámetros de autenticación pero aún no hay sesión activa en el cliente.
        // Esperamos a que onAuthStateChange procese el token.
        console.log("useAuth Context: Auth params detected, waiting for onAuthStateChange to process...");
        authTimeout = setTimeout(() => {
          if (!isSubscribed) return;
          console.log("useAuth Context: Auth fallback timeout reached. Forcing loading to false.");
          setAuthState(prev => ({
            ...prev,
            loading: false
          }));
        }, 3500);
      } else {
        // No hay parámetros en la URL o ya tenemos una sesión válida.
        // Podemos quitar el estado de carga inmediatamente.
        setAuthState(prev => ({
          ...prev,
          session,
          user: session?.user ?? null,
          loading: false
        }));

        if (session?.user) {
          loadUserProfile(session.user.id);
        }
      }
    });

    // 2. Escuchar todos los cambios de autenticación
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isSubscribed) return;
        
        console.log("useAuth Context: onAuthStateChange fired -> event =", event, "session =", !!session);

        // Si se establece una sesión o hay un cambio de estado válido
        if (session) {
          if (authTimeout) {
            console.log("useAuth Context: Session established. Clearing authTimeout.");
            clearTimeout(authTimeout);
            authTimeout = null;
          }
        }

        // Para cualquier evento excepto si estamos esperando activamente los parámetros del token,
        // nos aseguramos de quitar el loading.
        const shouldKeepLoading = hasAuthParams && !session && event === 'INITIAL_SESSION';

        setAuthState(prev => {
          const newLoading = shouldKeepLoading ? prev.loading : false;
          return {
            ...prev,
            session,
            user: session?.user ?? null,
            loading: newLoading
          };
        });

        if (session?.user) {
          await loadUserProfile(session.user.id);
        } else {
          setAuthState(prev => ({ ...prev, profile: null }));
        }
      }
    );

    return () => {
      isSubscribed = false;
      if (authTimeout) {
        clearTimeout(authTimeout);
      }
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string,
    password: string,
    userData: {
      firstName: string;
      lastName: string;
      company?: string;
    },
    options?: { emailRedirectTo?: string }
  ) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: options?.emailRedirectTo,
          data: {
            first_name: userData.firstName,
            last_name: userData.lastName,
            company: userData.company || null
          }
        }
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;

      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    try {
      if (!authState.user) throw new Error('No user logged in');

      const { data, error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', authState.user.id)
        .select()
        .single();

      if (error) throw error;

      setAuthState(prev => ({ ...prev, profile: data }));
      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  };

  const updatePassword = async (password: string) => {
    console.log("useAuth Context: updatePassword called");
    try {
      if (!authState.user) {
        console.warn("useAuth Context: internal state has no user. Checking supabase.auth.getUser() directly...");
      }

      const { data: { user }, error: userError } = await supabase.auth.getUser();

      if (userError || !user) {
        console.error("useAuth Context: No active session found on client", userError);
        throw new Error("No hay una sesión activa. El enlace de recuperación puede haber expirado o las cookies están bloqueadas.");
      }

      console.log("useAuth Context: calling supabase.auth.updateUser for user", user.id);
      const { data, error } = await supabase.auth.updateUser({
        password
      });
      console.log("useAuth Context: supabase.auth.updateUser result", { data, error });

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      console.error("useAuth Context: updatePassword error", error);
      return { data: null, error };
    }
  };

  const contextValue: AuthContextType = {
    ...authState,
    signUp,
    signIn,
    signOut,
    updateProfile,
    resetPassword,
    updatePassword,
    isAuthenticated: !!authState.user
  };

  return createElement(AuthContext.Provider, { value: contextValue }, children);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}